import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  IconButton,
  Chip,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

/**
 * ModalSelector - Componente reutilizable para seleccionar items de una lista con búsqueda
 *
 * @param {string|number} value - Valor seleccionado (ID del item)
 * @param {function} onChange - Callback cuando cambia la selección, recibe el ID seleccionado
 * @param {array} items - Array de objetos con la estructura definida por itemValue e itemLabel
 * @param {string} itemValue - Nombre de la propiedad que contiene el ID del item (default: "id")
 * @param {string} itemLabel - Nombre de la propiedad que contiene la descripción del item (default: "descripcion")
 * @param {string} label - Etiqueta del campo
 * @param {string} placeholder - Texto placeholder del input
 * @param {string} searchPlaceholder - Texto placeholder del buscador en el modal
 * @param {string} emptyText - Texto cuando no hay resultados
 * @param {string} noneOptionText - Texto para la opción "ninguno"
 * @param {boolean} fullWidth - Si el input ocupa todo el ancho
 * @param {boolean} disabled - Si el componente está deshabilitado
 * @param {boolean} showClearButton - Si muestra el botón de limpiar
 * @param {boolean} showNoneOption - Si muestra la opción "Ninguna"
 */
const ModalSelector = ({
  value,
  onChange,
  items = [],
  itemValue = "id",
  itemLabel = "descripcion",
  label = "Seleccionar",
  placeholder = "Seleccione una opción",
  searchPlaceholder = "Buscar...",
  emptyText = "No se encontraron resultados",
  noneOptionText = "Ninguna",
  fullWidth = true,
  disabled = false,
  showClearButton = true,
  showNoneOption = true,
}) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredItems, setFilteredItems] = useState([])

  // Get selected item description
  const selectedItem = items.find((item) => item[itemValue] === value)
  const displayValue = selectedItem ? selectedItem[itemLabel] : ""

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredItems(items)
    } else {
      const filtered = items.filter((item) =>
        item[itemLabel].toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredItems(filtered)
    }
  }, [searchTerm, items, itemLabel])

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true)
      setSearchTerm("")
    }
  }

  const handleClose = () => {
    setOpen(false)
    setSearchTerm("")
  }

  const handleSelect = (itemId) => {
    onChange(itemId)
    handleClose()
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange("")
  }

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        label={label}
        value={displayValue}
        placeholder={placeholder}
        onClick={handleOpen}
        disabled={disabled}
        variant="outlined"
        InputProps={{
          readOnly: true,
          sx: {
            cursor: disabled ? "not-allowed" : "pointer",
            "& input": {
              cursor: disabled ? "not-allowed" : "pointer",
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              {showClearButton && value && !disabled && (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  sx={{
                    mr: 0.5,
                    color: "#344767",
                    "&:hover": {
                      color: "#1976d2",
                      backgroundColor: "rgba(25, 118, 210, 0.08)"
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <ArrowDropDownIcon sx={{ color: "#344767" }} />
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "400px",
            maxHeight: "600px",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            Seleccionar {label}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              autoFocus
              InputProps={{
                sx: {
                  color: "#344767",
                  "& input": {
                    color: "#344767",
                  },
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#344767" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ color: "#344767" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </MDBox>

          {filteredItems.length > 0 || showNoneOption ? (
            <List sx={{ pt: 0 }}>
              {showNoneOption && (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSelect("")} selected={value === ""}>
                    <ListItemText
                      primary={noneOptionText}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { color: "#344767" },
                      }}
                    />
                    {value === "" && <Chip label="Seleccionada" size="small" color="primary" />}
                  </ListItemButton>
                </ListItem>
              )}
              {filteredItems.map((item) => (
                <ListItem key={item[itemValue]} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelect(item[itemValue])}
                    selected={value === item[itemValue]}
                  >
                    <ListItemText
                      primary={item[itemLabel]}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { color: "#344767" },
                      }}
                    />
                    {value === item[itemValue] && (
                      <Chip label="Seleccionada" size="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <MDTypography variant="body2" color="text" fontWeight="light">
                {emptyText}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

ModalSelector.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  items: PropTypes.array,
  itemValue: PropTypes.string,
  itemLabel: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyText: PropTypes.string,
  noneOptionText: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  showClearButton: PropTypes.bool,
  showNoneOption: PropTypes.bool,
}

export default ModalSelector
